import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { readTabUserData } from "../../utils/tabAuthStorage";

import {

  Alert,

  AlertTitle,

  Box,

  Chip,

  CircularProgress,

  IconButton,

  Snackbar,

  Stack,

  Tooltip,

  Typography,

  alpha,

  useTheme,

} from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OpenInPopupIconButton from "../common/OpenInPopupIconButton";
import { openTeamPopupWindow } from "../../utils/openTeamPopupWindow";

import { useLeadTeamSpace } from "../../hooks/useLeadTeamSpace";

import { useTeamMessages } from "../../hooks/useTeamMessages";

import { useTeamSpaceDetails, useTeamSpaces } from "../../hooks/useTeamSpaces";

import { useRealtimeTeamMessages } from "../../hooks/useRealtimeTeamMessages";

import { useRealtimeTeamTyping } from "../../hooks/useRealtimeTeamTyping";

import { useNotificationsOptional } from "../../context/NotificationContext";

import TeamMessageList from "../../pages/Team/components/TeamMessageList";

import TeamMessageInput from "../../pages/Team/components/TeamMessageInput";

import TeamTypingIndicator from "../../pages/Team/components/TeamTypingIndicator";

import FileDropHandler from "../MessageHistory/FileDropHandler";

import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";

import { teamPath } from "../../utils/teamNavigation";

const SIDEBAR_CHAT_MIN_HEIGHT = 320;



const RequestTeamDiscussionPanel = ({ conversationId, active = false }) => {

  const { t } = useTranslation("team");

  const theme = useTheme();

  const navigate = useNavigate();

  const composerRef = useRef(null);

  const leadId = conversationId ? Number(conversationId) : null;



  const [feedback, setFeedback] = useState(null);



  const currentUserId = useMemo(() => {
    try {
      return readTabUserData()?.id ?? null;
    } catch {
      return null;
    }
  }, []);



  const skipNextSpaceLoadRef = useRef(false);

  const { spaceId, loading: spaceLoading, error: spaceError, ensureCreated } = useLeadTeamSpace(leadId, {

    enabled: active && Boolean(leadId),

  });

  const { markRead, refetch: refetchSpaces } = useTeamSpaces();
  const notificationsCtx = useNotificationsOptional();



  const { data: spaceDetails } = useTeamSpaceDetails(spaceId);

  const members = spaceDetails?.members || [];

  const space = spaceDetails?.space || (leadId
    ? { type: "channel", slug: `solicitacao-${leadId}`, isPrivate: true }
    : null);



  const {

    messages,

    loadMessages,

    loadMore,

    syncLatest,

    sendMessage,

    editMessage,

    deleteMessage,

    isSending,

    isLoading,

    loadError,

    hasMore,

    reset,

  } = useTeamMessages(spaceId);



  useEffect(() => {

    if (!active || !spaceId) return;

    if (skipNextSpaceLoadRef.current) {
      skipNextSpaceLoadRef.current = false;
      markRead(spaceId)
        .catch(() => {})
        .finally(() => refetchSpaces());
      return;
    }

    reset();

    loadMessages().catch(() => {});

    markRead(spaceId)
      .catch(() => {})
      .finally(() => refetchSpaces());

  }, [active, spaceId, reset, loadMessages, markRead, refetchSpaces]);



  const registerTeamFloatingSpace = notificationsCtx?.registerTeamFloatingSpace;

  useEffect(() => {

    if (!active || !spaceId) return undefined;

    registerTeamFloatingSpace?.(spaceId, true);

    return () => registerTeamFloatingSpace?.(spaceId, false);

  }, [active, spaceId, registerTeamFloatingSpace]);



  useRealtimeTeamMessages({

    selectedSpaceId: active ? spaceId : null,

    syncLatestMessages: syncLatest,

    refetchSpaces,

    markRead,

    markReadEnabled: active,

  });

  const { typingUsers } = useRealtimeTeamTyping({

    spaceId,

    currentUserId,

  });



  const showActionError = useCallback(

    (err, fallbackMessage, fallbackHint) => {

      const { message, action } = getUserFacingApiMessage(err);

      const resolvedMessage =
        !message || message === "Não foi possível concluir a operação."
          ? fallbackMessage
          : message;

      setFeedback({

        severity: "error",

        message: resolvedMessage,

        hint: action || fallbackHint || null,

      });

    },

    []

  );



  const handleSend = useCallback(

    async (payload) => {

      try {

        if (payload?.editMessageId) {

          const { editMessageId, ...rest } = payload;

          await editMessage({ messageId: editMessageId, ...rest });

          await syncLatest({ refreshRecent: true });

        } else {

          let sid = spaceId;
          if (!sid) {
            skipNextSpaceLoadRef.current = true;
            try {
              const created = await ensureCreated();
              sid = created?.id != null ? Number(created.id) : null;
              if (!sid) {
                skipNextSpaceLoadRef.current = false;
                throw new Error(t("sidebarTeamSendError"));
              }
            } catch (createErr) {
              skipNextSpaceLoadRef.current = false;
              throw createErr;
            }
          }

          await sendMessage(payload, sid);

          await syncLatest();

        }

      } catch (err) {

        console.error("[RequestTeamDiscussionPanel] send:", err);

        showActionError(err, t("sidebarTeamSendError"), t("sidebarTeamSendErrorHint"));

        throw err;

      }

    },

    [spaceId, ensureCreated, sendMessage, editMessage, syncLatest, showActionError, t]

  );



  const handleDelete = useCallback(

    async (msg) => {

      if (!msg?.id) return;

      if (!window.confirm(t("deleteMessageConfirm"))) return;

      try {

        await deleteMessage(msg.id);

        await syncLatest({ refreshRecent: true });

      } catch (err) {

        console.error("[RequestTeamDiscussionPanel] delete:", err);

        showActionError(err, t("sidebarTeamDeleteError"), t("sidebarTeamDeleteErrorHint"));

      }

    },

    [deleteMessage, syncLatest, showActionError, t]

  );



  if (!leadId) return null;



  if (!active) {

    return (

      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>

        {t("sidebarTeamCollapsedHint")}

      </Typography>

    );

  }



  if (spaceLoading && !spaceId) {

    return (

      <Box

        sx={{

          display: "flex",

          flexDirection: "column",

          alignItems: "center",

          justifyContent: "center",

          py: 4,

          gap: 1.5,

        }}

      >

        <CircularProgress size={28} />

        <Typography variant="body2" color="text.secondary">

          {t("loading")}

        </Typography>

      </Box>

    );

  }



  if (spaceError) {
    const { message: spaceErrMessage, action: spaceErrAction } = getUserFacingApiMessage(spaceError);

    return (

      <Alert severity="warning" sx={{ borderRadius: 2 }}>

        <AlertTitle sx={{ fontWeight: 700 }}>
          {spaceErrMessage && spaceErrMessage !== "Não foi possível concluir a operação."
            ? spaceErrMessage
            : t("sidebarTeamLoadError")}
        </AlertTitle>

        <Typography variant="body2" sx={{ lineHeight: 1.55 }}>

          {spaceErrAction || t("sidebarTeamLoadErrorHint")}

        </Typography>

      </Alert>

    );

  }



  const memberCount = members.length;



  return (
    <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
      <Stack spacing={0.75} sx={{ mb: 1, width: "100%", minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Chip
            icon={<ForumOutlinedIcon sx={{ fontSize: "15px !important" }} />}
            label={t("sidebarTeamInternalBadge")}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: 700,
              flex: "1 1 auto",
              minWidth: 0,
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title={t("openInTeamPageHint")}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => spaceId && navigate(teamPath(spaceId))}
                  disabled={!spaceId}
                  aria-label={t("openInTeamPage")}
                  sx={{ p: 0.75 }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
            <OpenInPopupIconButton
              title={t("openPopupWindowTooltip")}
              ariaLabel={t("openPopupWindow")}
              disabled={!spaceId}
              onClick={() => spaceId && openTeamPopupWindow(spaceId)}
              sx={{ p: 0.75, border: "none" }}
            />
          </Stack>
        </Stack>
        {memberCount > 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {t("channelMemberCount", { count: memberCount })}
          </Typography>
        ) : null}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1, lineHeight: 1.45 }}
      >
        {t("sidebarTeamIntro")}
      </Typography>

      <FileDropHandler
        onFileUpload={(fileOrFiles) => composerRef.current?.appendFiles(fileOrFiles)}
        sx={{
          width: "100%",
          minWidth: 0,
          minHeight: SIDEBAR_CHAT_MIN_HEIGHT,
          height: SIDEBAR_CHAT_MIN_HEIGHT,
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: alpha(theme.palette.action.hover, 0.04),
          boxShadow: "none",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column" }}>

          <TeamMessageList

            listKey={spaceId}

            spaceId={spaceId}

            messages={messages}

            currentUserId={currentUserId}

            spaceType="channel"

            compact

            onLoadMore={loadMore}

            hasMore={hasMore}

            isLoading={isLoading}

            loadError={loadError}

            isSending={isSending}

            onDelete={handleDelete}

          />

        </Box>



        <TeamTypingIndicator typingUsers={typingUsers} compact />



        <TeamMessageInput

          ref={composerRef}

          onSend={handleSend}

          isSending={isSending}

          leadId={leadId}

          currentUserId={currentUserId}

          members={members}

          space={space}

          embedded

        />

      </FileDropHandler>



      <Snackbar

        open={Boolean(feedback)}

        autoHideDuration={9000}

        onClose={() => setFeedback(null)}

        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}

      >

        <Alert

          severity={feedback?.severity || "error"}

          variant="filled"

          onClose={() => setFeedback(null)}

          sx={{ width: "100%", maxWidth: 400 }}

        >

          {feedback?.message}

          {feedback?.hint ? (

            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.92, lineHeight: 1.5 }}>

              {feedback?.hint}

            </Typography>

          ) : null}

        </Alert>

      </Snackbar>

    </Box>

  );

};



export default RequestTeamDiscussionPanel;

